/**
 * Location Tracking Service
 * Tracks player positions for multiplayer encounters and dynamic companion spawning
 */

import { supabaseClient } from './supabaseClient';

export interface PlayerLocation {
  playerId: string;
  playerName: string;
  locationId: string;
  locationName: string;
  roomId: string | null;
  enteredAt: Date;
  currentActivity: string;
  karma: number;
}

export interface GameLocation {
  id: string;
  name: string;
  description: string;
  type: 'safe' | 'dangerous' | 'neutral' | 'social' | 'hidden';
  encounterRate: number; // 0-1, probability of companion/player spawn
  availableCompanionTypes: string[];
  maxPlayers: number;
}

class LocationTrackingService {
  private currentLocation: string | null = null;
  private updateInterval: NodeJS.Timeout | null = null;

  /**
   * Update player's current location
   */
  async updatePlayerLocation(
    playerId: string,
    playerName: string,
    locationId: string,
    locationName: string,
    roomId: string | null,
    activity: string,
    karma: number
  ): Promise<void> {
    try {
      const { error } = await supabaseClient
        .from('player_locations')
        .upsert({
          player_id: playerId,
          player_name: playerName || 'Unknown Dreamer',
          location_id: locationId,
          location_name: locationName,
          room_id: roomId,
          entered_at: new Date().toISOString(),
          current_activity: activity,
          karma: karma,
          last_heartbeat: new Date().toISOString()
        });

      if (error) {
        console.warn('[LocationTracking] Failed to update location:', error);
      } else {
        console.log(`[LocationTracking] Updated location to: ${locationName}`);
        this.currentLocation = locationId;
      }
    } catch (error) {
      console.error('[LocationTracking] Error updating location:', error);
    }
  }

  /**
   * Get all players in the same location
   */
  async getPlayersAtLocation(
    locationId: string,
    roomId?: string | null,
    excludePlayerId?: string
  ): Promise<PlayerLocation[]> {
    try {
      let query = supabaseClient
        .from('player_locations')
        .select('*')
        .eq('location_id', locationId)
        // Only show players active in last 5 minutes
        .gte('last_heartbeat', new Date(Date.now() - 5 * 60 * 1000).toISOString());

      if (roomId) {
        query = query.eq('room_id', roomId);
      }

      if (excludePlayerId) {
        query = query.neq('player_id', excludePlayerId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[LocationTracking] Error getting players:', error);
        return [];
      }

      return (data || []).map(row => ({
        playerId: row.player_id,
        playerName: row.player_name,
        locationId: row.location_id,
        locationName: row.location_name,
        roomId: row.room_id,
        enteredAt: new Date(row.entered_at),
        currentActivity: row.current_activity,
        karma: row.karma
      }));
    } catch (error) {
      console.error('[LocationTracking] Error querying players:', error);
      return [];
    }
  }

  /**
   * Start heartbeat to keep location fresh
   */
  startHeartbeat(playerId: string): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }

    this.updateInterval = setInterval(async () => {
      if (this.currentLocation) {
        try {
          await supabaseClient
            .from('player_locations')
            .update({ last_heartbeat: new Date().toISOString() })
            .eq('player_id', playerId);
        } catch (error) {
          console.warn('[LocationTracking] Heartbeat failed:', error);
        }
      }
    }, 30000); // Every 30 seconds
  }

  /**
   * Stop heartbeat
   */
  stopHeartbeat(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  /**
   * Remove player from location (on exit)
   */
  async removePlayer(playerId: string): Promise<void> {
    try {
      await supabaseClient
        .from('player_locations')
        .delete()
        .eq('player_id', playerId);

      console.log('[LocationTracking] Player removed from location');
    } catch (error) {
      console.error('[LocationTracking] Error removing player:', error);
    }
  }

  /**
   * Extract location from story narrative using AI
   */
  extractLocationFromNarrative(narrative: string): string {
    // Simple extraction - look for location keywords
    const locationPatterns = [
      /in (the )?([A-Z][a-z]+ ?)+/,
      /at (the )?([A-Z][a-z]+ ?)+/,
      /inside (the )?([A-Z][a-z]+ ?)+/,
      /(Neo-[A-Z][a-z]+)/,
      /(Observatory|Plaza|District|Sector|Hub|Temple|Market)/i
    ];

    for (const pattern of locationPatterns) {
      const match = narrative.match(pattern);
      if (match) {
        return match[0].replace(/^(in|at|inside) (the )?/i, '').trim();
      }
    }

    return 'Unknown Location';
  }
}

export const locationTrackingService = new LocationTrackingService();
